"use client";

import { SingleMiddleColumnLayout } from "@/components/layouts";
import { StandardBody, StandardSubmitButton, StandardTitle } from "@/components/standards";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { createSupabaseClientForBrowser } from "@/utils/supabase/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { isValidPhoneNumber, parsePhoneNumber } from "libphonenumber-js";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
const phoneSchema = z.object({
  phone: z.string().refine(val => isValidPhoneNumber(val, "US"), {
    message: "Please enter a valid US phone number"
  })
});
const otpSchema = z.object({
  code: z.string().length(6, "Please enter a valid 6-digit code")
});
async function checkUserInfo() {
  const supabase = createSupabaseClientForBrowser();
  const {
    data: {
      user
    }
  } = await supabase.auth.getUser();
  if (!user) throw new Error("User not found");
  const {
    data,
    error
  } = await supabase.from("user_info").select("email").eq("id", user.id).single();
  if (error) throw error;
  return data;
}
export function PhoneLogin() {
  const [phone, setPhone] = useState("");
  const [showVerification, setShowVerification] = useState(false);
  const supabase = createSupabaseClientForBrowser();
  const router = useRouter();
  const phoneForm = useForm({
    resolver: zodResolver(phoneSchema),
    defaultValues: {
      phone: ""
    }
  });
  async function onPhoneSubmit(data: z.infer<typeof phoneSchema>) {
    try {
      const parsed = parsePhoneNumber(data.phone, "US");
      const formattedPhone = parsed.format("E.164");
      const {
        error
      } = await supabase.auth.signInWithOtp({
        phone: formattedPhone,
        options: {
          shouldCreateUser: false
        }
      });
      if (error) {
        if (error.message === "Signups not allowed for otp") {
          phoneForm.setError("phone", {
            message: "We couldn't find an account with this phone number. If you're a new patient, please sign up first. If you're a returning patient and having trouble logging in, please email support@eurekahealth.com for help."
          });
          return;
        }
        phoneForm.setError("phone", {
          message: error.message
        });
        return;
      }
      setPhone(formattedPhone);
      setShowVerification(true);
    } catch (error) {
      phoneForm.setError("phone", {
        message: "Invalid phone number format"
      });
    }
  }
  if (showVerification) {
    return <VerificationStep phone={phone} onSuccess={redirectPath => router.push(redirectPath)} onResend={() => onPhoneSubmit(phoneForm.getValues())} />;
  }
  return <SingleMiddleColumnLayout data-sentry-element="SingleMiddleColumnLayout" data-sentry-component="PhoneLogin" data-sentry-source-file="PhoneLogin.tsx">
      <StandardTitle data-sentry-element="StandardTitle" data-sentry-source-file="PhoneLogin.tsx">Log in with phone</StandardTitle>
      <StandardBody data-sentry-element="StandardBody" data-sentry-source-file="PhoneLogin.tsx">
        Enter your phone number to receive a verification code.
      </StandardBody>

      <Form {...phoneForm} data-sentry-element="Form" data-sentry-source-file="PhoneLogin.tsx">
        <form onSubmit={phoneForm.handleSubmit(onPhoneSubmit)} className="space-y-6">
          <FormField control={phoneForm.control} name="phone" render={({
          field
        }) => <FormItem>
                <FormControl>
                  <Input type="tel" placeholder="555-555-5555" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="PhoneLogin.tsx" />

          <StandardSubmitButton isLoading={phoneForm.formState.isSubmitting} className="w-full" data-sentry-element="StandardSubmitButton" data-sentry-source-file="PhoneLogin.tsx">
            Send Code
          </StandardSubmitButton>
        </form>
      </Form>

      <Link href="/welcome" className="mt-4 block text-center text-sm hover:text-foreground" data-sentry-element="Link" data-sentry-source-file="PhoneLogin.tsx">
        ← Go back to signup
      </Link>
    </SingleMiddleColumnLayout>;
}
interface VerificationStepProps {
  phone: string;
  onSuccess: (redirectPath: string) => void;
  onResend: () => void;
}
function VerificationStep({
  phone,
  onSuccess,
  onResend
}: VerificationStepProps) {
  const supabase = createSupabaseClientForBrowser();
  const [isVerifying, setIsVerifying] = useState(false);
  const form = useForm({
    resolver: zodResolver(otpSchema),
    defaultValues: {
      code: ""
    }
  });
  async function onSubmit(data: z.infer<typeof otpSchema>) {
    try {
      setIsVerifying(true);
      const {
        error
      } = await supabase.auth.verifyOtp({
        phone,
        token: data.code,
        type: "sms"
      });
      if (error) {
        form.setError("code", {
          message: error.message
        });
        setIsVerifying(false);
        return;
      }

      // Check user_info table
      try {
        const userInfo = await checkUserInfo();
        onSuccess(userInfo?.email ? "/dash" : "/intake2-basic");
      } catch (error) {
        // If there's an error checking user_info, default to intake
        onSuccess("/intake2-basic");
      }
    } catch (error) {
      form.setError("code", {
        message: "Verification failed. Please try again."
      });
      setIsVerifying(false);
    }
  }
  return <SingleMiddleColumnLayout data-sentry-element="SingleMiddleColumnLayout" data-sentry-component="VerificationStep" data-sentry-source-file="PhoneLogin.tsx">
      <StandardTitle data-sentry-element="StandardTitle" data-sentry-source-file="PhoneLogin.tsx">Enter verification code</StandardTitle>
      <StandardBody data-sentry-element="StandardBody" data-sentry-source-file="PhoneLogin.tsx">
        Enter the 6-digit code sent to{" "}
        {parsePhoneNumber(phone, "US").formatNational()}
      </StandardBody>

      <Form {...form} data-sentry-element="Form" data-sentry-source-file="PhoneLogin.tsx">
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField control={form.control} name="code" render={({
          field
        }) => <FormItem className="flex flex-col items-center">
                <FormControl>
                  <InputOTP maxLength={6} value={field.value} onChange={value => {
              field.onChange(value);
              form.clearErrors();
            }} autoFocus>
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </FormControl>
                <FormMessage />
              </FormItem>} data-sentry-element="FormField" data-sentry-source-file="PhoneLogin.tsx" />

          <div className="space-y-4">
            <StandardSubmitButton isLoading={form.formState.isSubmitting || isVerifying} className="w-full" data-sentry-element="StandardSubmitButton" data-sentry-source-file="PhoneLogin.tsx">
              Verify Code
            </StandardSubmitButton>

            <Button type="button" variant="ghost" className="w-full" onClick={onResend} disabled={form.formState.isSubmitting || isVerifying} data-sentry-element="Button" data-sentry-source-file="PhoneLogin.tsx">
              Resend Code
            </Button>
          </div>
        </form>
      </Form>
    </SingleMiddleColumnLayout>;
}